import Avaliacao, { AvaliacaoStatus } from '../models/Avaliacao';
import { TIPO_ESCOLHA, TIPO_FOTO, TIPO_INTEIRO, TIPO_MULTIPLAESCOLHA, TIPO_NUMERICO } from '../models/QuestionarioQuestao';
import { converterNumero } from '../utils/validacao';
import ApiController from './ApiController';
import PermissaoController from './PermissaoController';

/** Definições de tipos */

/** Importando tipos utilizados na documentação 
 * @typedef {import('../models/AvaliacaoSecao').default} AvaliacaoSecao
 * @typedef {import('../models/AvaliacaoResposta').default} AvaliacaoResposta
 */

/**
 * Dados para inclusão de uma avaliação
 * @typedef AvaliacaoInclusao
 * @property {Number} empresa Identificação da empresa vinculada à avaliação
 * @property {Number} cliente Identificação do cliente vinculado à avaliação
 * @property {Number} questionario Identificação do questionário vinculado à avaliação
 */

/**
 * Mensagens de erro de uma avaliação
 * @typedef AvaliacaoInclusaoErros
 * @property {String} empresa Mensagem de erro do campo empresa
 * @property {String} cliente Mensagem de erro do campo cliente
 * @property {String} questionario Mensagem de erro do campo questionário
 * @property {String} erroAvaliacao Mensagem de erro geral da avaliação
 * @property {Boolean} valido Indica se o objeto está válido ou não
 */

/**
 * Mensagens de erro de uma avaliação
 * @typedef AvaliacaoAlteracaoErros
 * @property {String} erroAvaliacao Mensagem de erro geral da avaliação
 * @property {String} erroSecoes Mensagem de erro geral das seções
 * @property {String} status Mensagem de erro do status
 * @property {Array<AvaliacaoSecoesErros>} secoes Lista com os erros das seções
 * @property {Boolean} valido Indica se o objeto está válido ou não
 */

/**
 * Mensagens de erro de uma seção da avaliação
 * @typedef AvaliacaoSecoesErros
 * @property {String} erroSecao Mensagem de erro geral da seção
 * @property {String} erroRespostas Mensagem de erro geral das questões
 * @property {String} id Mensagem de erro do Id da seção
 * @property {Array<AvaliacaoRespostaErros>} respostas Lista com os erros das seções
 * @property {Boolean} valido Indica se o objeto está válido ou não
 */

/**
 * Mensagens de erro de uma resposta da avaliação
 * @typedef AvaliacaoRespostaErros
 * @property {String} erroResposta Mensagem de erro geral da resposta
 * @property {String} erroOpcoes Mensagem de erro geral das opções de resposta
 * @property {String} id Mensagem de erro do Id da questão
 * @property {String|Array<String>} resposta Mensagem de erro da resposta
 * @property {Boolean} valido Indica se o objeto está válido ou não
 */

/**
 * Objeto contendo os dados a serem repassados para a API
 * @typedef AvaliacaoAlteracao
 * @property {String} status Novo status da avaliação
 * @property {Array<AvaliacaoSecaoAlteracao>} secoes Lista de seções da avaliação
 */

/**
 * Objeto contendo os dados da seção a ser enviada para a API
 * @typedef AvaliacaoSecaoAlteracao
 * @property {Number} id Identificação da seção
 * @property {Array<AvaliacaoRespostaAlteracao>} respostas Lista de respostas da seção
 */

/**
 * Objeto contendo os dados da resposta a ser enviada para a API
 * @typedef AvaliacaoRespostaAlteracao
 * @property {Number} id Identificação da questão
 * @property {Number|String|Array<Number>} resposta Resposta para a questão
 * @property {String} outro Resposta alternativa para questões de múltipla escolha
 */

/**
 * Retorno do método
 * @typedef Retorno
 * @property {Boolean} sucesso Indica se o método foi executado com êxito
 * @property {ErroAvaliacao} erros Mensagem de erro ou objeto com as validações de cada campo
 * @property {Avaliacao} avaliacao Dados da avaliação
 */

/**
 * Retorno do método listar
 * @typedef RetornoListar
 * @property {Boolean} sucesso Indica se o método foi executado com êxito
 * @property {Number} count Quantidade de itens encontrados
 * @property {Number} page Número da página recuperada
 * @property {Number} pageCount Quantidade de páginas
 * @property {Number} pageSize Quantidade de itens por página
 * @property {Array<Avaliacao>} items Lista com os itens encontrados
 */

/**
 * Controlador das requisições da API de avaliação
 */
class AvaliacaoController {

    constructor() {
        this.apiController = new ApiController();
        this.permissaoController = new PermissaoController();
    }

    /**
     * Lista as avaliações de acordo com os parâmetros informados
     * 
     * @param {Number} page Número da página a ser retornada
     * @param {Number} pageSize Quantidade de registros por página
     * @param {Object} params Parâmetros utilizados para filtrar os dados
     * @param {String} param.questionario Título do questionário
     * @param {String} param.cliente Nome do cliente
     * @param {String} param.avaliador Nome do avaliador
     * @param {String} param.status Status da avaliação
     *
     * @return {Promise<RetornoListar>} Retorna as avaliações encontradas
     */
    async listar(page, pageSize, { questionario, cliente, avaliador, status }) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/avaliacao', { page, pageSize, questionario, cliente, avaliador, status });
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            //Mapeia os itens para objetos do tipo Avaliacao
            const items = result.items.map(item => Avaliacao.clone(item));
            result.items = items;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            const erros = error.response ? error.response.data : [{ mensagem: error.message || 'Erro de conexão com o servidor' }];
            return { sucesso: false, erros };
        }
    }

    /**
     * Retorna os dados de uma avaliação específica, com as seções e questões relacionadas
     * @param {Number} id Id da avaliação a ser consultada
     * 
     * @return {Promise<Retorno>} Retorna a avaliação solicitada
     */
    async obter(id) {
        //Verifica se o ID está preenchido
        if (!id)
            return { sucesso: false, erros: [{ campo: 'id', mensagem: 'É necessário' }] };

        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/avaliacao/' + id.toString());
            //Retorna indicando que houve sucesso e os dados da avaliacação
            return { sucesso: true, avaliacao: Avaliacao.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Inicia uma nova avaliação para o cliente especificado
     * @param {AvaliacaoInclusao} avaliacao Dados da avaliação a ser iniciada
     * 
     * @returns {Promise<Retorno>} Retorna a avaliação inserida, com as seções e questões para ser respondida
     */
    async salvar(avaliacao) {
        const erros = await this._validarInclusao(avaliacao);

        //Retorna os erros para a aplicação
        if (!erros.valido)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = await this._extrairCamposInclusao(avaliacao);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.post('/avaliacao', body);
            //Retorna indicando sucesso e cria uma cópia da avaliação
            return { sucesso: true, avaliacao: Avaliacao.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    async atualizar(avaliacao) {
        const erros = this._validarAlteracao(avaliacao);

        if (!erros.valido)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = this._extrairCamposAlteracao(avaliacao);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.patch('/avaliacao/' + avaliacao.id, body);
            //Retorna indicando sucesso e cria uma cópia da avaliação
            return { sucesso: true, avaliacao: Avaliacao.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Lista as avaliações concluídas para que o usuário selecione
     * @param {Number} idCliente Código do cliente para filtragem das avaliações
     * @param {Number} idQuestionario Código do questionário para filtragem das avaliações
     * @returns {Promise<RetornoListar>} Retorna uma lista com as avaliações identificadas
     */
    async listarSelecao(idCliente, idQuestionario) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: items } = await this.apiController.get('/avaliacao', { idQuestionario, idCliente, status: 'finalizada', pagination: false, includeDetails: false });
            const result = {
                //Marca que houve retorno com sucesso
                sucesso: true,
                //Mapeia os itens para objetos do tipo Avaliacao
                items: items.map(item => Avaliacao.clone(item))
            };
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Lista as avaliações concluídas para geração do relatório
     * @param {Object} param Parâmetros para a pesquisa
     * @param {Number} param.idCliente Código do cliente para filtragem das avaliações
     * @param {Number} param.idQuestionario Código do questionário para filtragem das avaliações
     * @param {Number} param.quantidade Quantidade de avaliações a serem listadas
     * @param {Array<Number>} param.avaliacoes Lista de identificações das avaliações a serem pesquisadas
     * @returns {Promise<RetornoListar>} Retorna uma lista com as avaliações identificadas
     */
    async obterResultado({ idCliente, idQuestionario, quantidade, avaliacoes }) {
        try {
            //Monta os parâmetros da requisição
            const parametros = {
                idQuestionario,
                idCliente,
                avaliacoes,
                last: quantidade,
                status: 'finalizada',
                pagination: false,
                includeDetails: true
            };
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: items } = await this.apiController.get('/avaliacao', parametros);
            const result = {
                //Marca que houve retorno com sucesso
                sucesso: true,
                //Mapeia os itens para objetos do tipo Avaliacao
                items: items.map(item => Avaliacao.clone(item))
            };
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Retorna o PDF do relatório para que usuário possa salvá-lo
     * @param {Object} param Parâmetros para a pesquisa
     * @param {Number} param.idCliente Código do cliente para filtragem das avaliações
     * @param {Number} param.idQuestionario Código do questionário para filtragem das avaliações
     * @param {Number} param.quantidade Quantidade de avaliações a serem listadas
     * @param {Array<Number>} param.avaliacoes Lista de identificações das avaliações a serem pesquisadas
     * @returns {Promise<String>} Retorna o conteúdo do arquivo PDF
     */
    async obterResultadoPDF({ idCliente, idQuestionario, quantidade, avaliacoes }) {
        try {
            //Monta os parâmetros da requisição
            const parametros = {
                idQuestionario,
                idCliente,
                avaliacoes,
                last: quantidade,
                formato: 'pdf',
                status: 'finalizada',
                pagination: false,
                includeDetails: true
            };
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: conteudo } = await this.apiController.get('/avaliacao', parametros, true, 'arraybuffer');
            return { sucesso: true, conteudo };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Envia uma requisição para envio de e-mail para o cliente
     * @param {Object} param Parâmetros para a pesquisa
     * @param {Number} param.idCliente Código do cliente para filtragem das avaliações
     * @param {Number} param.idQuestionario Código do questionário para filtragem das avaliações
     * @param {Number} param.quantidade Quantidade de avaliações a serem listadas
     * @param {Array<Number>} param.avaliacoes Lista de identificações das avaliações a serem pesquisadas
     * @returns {Promise<Object>} Retorna a indicação se a requisição foi realizada com sucesso
     */
    async enviarEmail({ idCliente, idQuestionario, quantidade, avaliacoes }) {
        try {
            const parametros = {
                idQuestionario,
                idCliente,
                avaliacoes,
                last: quantidade,
                formato: 'mail',
                status: 'finalizada',
                pagination: false,
                includeDetails: true
            };
            //Envia requisição para o servidor e captura os dados de resposta
            await this.apiController.get('/avaliacao', parametros);
            //Retorna os dados
            return { sucesso: true };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Valida os dados da avaliação a ser iniciada
     * @param {AvaliacaoInclusao} avaliacao Dados da avaliação a ser validada
     * 
     * @returns {AvaliacaoInclusaoErros} Retorna um objeto contendo os erros de validação
     */
    async _validarInclusao(avaliacao) {
        const result = {
            empresa: '',
            cliente: '',
            questionario: '',
            erroAvaliacao: '',
            valido: true,
        };

        //Verifica se o parâmetro foi recebido
        if (!avaliacao) {
            result.erroAvaliacao = 'Avaliação é necessária';
            result.valido = false;
        } else {
            //Verifica os campos do parâmetro
            if (!avaliacao.cliente) {
                result.cliente = 'É necessário';
                result.valido = false;
            } else if (isNaN(avaliacao.cliente)) {
                result.cliente = 'Precisa ser numérico';
                result.valido = false;
            }

            if (!avaliacao.questionario) {
                result.questionario = 'É necessário';
                result.valido = false;
            } else if (isNaN(avaliacao.questionario)) {
                result.questionario = 'Precisa ser numérico';
                result.valido = false;
            }

            //Somente valida a empresa se o usuário possuir permissão para selecioná-la
            if (await this.permissaoController.permiteSelecionarEmpresa()) {
                if (!avaliacao.empresa) {
                    result.empresa = 'É necessário';
                    result.valido = false;
                } else if (isNaN(avaliacao.empresa)) {
                    result.empresa = 'Precisa ser numérico';
                    result.valido = false;
                }
            }
        }


        return result;
    }

    /**
     * Retorna um novo objeto apenas com os campos necessários para a requisição
     * @param {AvaliacaoInclusao} avaliacao Dados da avaliação a ser iniciada
     * @returns {AvaliacaoInclusao} Retorna um objeto sem campos extras
     */
    async _extrairCamposInclusao(avaliacao) {
        const result = {
            cliente: avaliacao.cliente,
            questionario: avaliacao.questionario
        };

        if (await this.permissaoController.permiteSelecionarEmpresa()) {
            result.empresa = avaliacao.empresa;
        }

        return result;
    }

    /**
     * Valida os dados da avaliação para preenchimento das respostas
     * @param {Avaliacao} avaliacao Dados da avaliação a ser validada
     * 
     * @return {AvaliacaoAlteracaoErros} Retorna um objeto com as mensagens de validação
     */
    _validarAlteracao(avaliacao) {
        /** @type {AvaliacaoAlteracaoErros} */
        const result = {
            erroAvaliacao: '',
            erroSecoes: '',
            status: '',
            secoes: [],
            valido: true,
        };

        if (!avaliacao) {
            result.erroAvaliacao = 'Avaliação é necessária';
            result.valido = false;
        } else {
            if (avaliacao.status !== undefined) {
                if (!avaliacao.status) {
                    result.status = 'É necessário';
                    result.valido = false;
                } else if (!AvaliacaoStatus.includes(avaliacao.status)) {
                    result.status = 'Não é um status válido';
                    result.valido = false;
                }
            }

            if (avaliacao.secoes) {
                if (!Array.isArray(avaliacao.secoes)) {
                    result.erroSecoes = 'Seções precisa ser um array';
                    result.valido = false;
                } else {
                    //Valida as seções individualmente
                    for (let i = 0; i < avaliacao.secoes.length; i++) {
                        //Valida a seção
                        let errosSecao = this._validarSecaoAlteracao(avaliacao.secoes[i]);
                        //Adiciona os erros na lista
                        result.secoes.push(errosSecao);
                        //Caso a seção possua erros, todo o objeto é marcado como inválido
                        if (!errosSecao.valido)
                            result.valido = false;
                    }
                }
            }
        }


        return result;
    }

    /**
     * Valida os dados de uma seção
     * @param {AvaliacaoSecao} secao Dados da seção a ser validada
     * 
     * @returns {AvaliacaoSecoesErros} Retorna um objeto contendo os erros da seção
     */
    _validarSecaoAlteracao(secao) {
        /** @type {AvaliacaoSecoesErros} */
        const result = {
            erroSecao: '',
            erroRespostas: '',
            id: '',
            respostas: [],
            valido: true,
        };

        if (!secao) {
            result.erroSecao = 'Seção é necessária';
            result.valido = false;
        } else {
            if (!secao.id) {
                result.id = 'É necessário';
                result.valido = false;
            }

            if (secao.questoes) {
                if (!Array.isArray(secao.questoes)) {
                    result.erroRespostas = 'Respostas precisa ser um array';
                    result.valido = false;
                } else {
                    //Valida as respostas individualmente
                    for (let i = 0; i < secao.questoes.length; i++) {
                        //Valida a resposta
                        let erroResposta = this._validarRespostaAlteracao(secao.questoes[i]);
                        //Adiciona os erros na lista
                        result.respostas.push(erroResposta);
                        //Caso a resposta possua erros, todo o objeto é marcado como inválido
                        if (!erroResposta.valido)
                            result.valido = false;
                    }
                }
            }
        }

        return result;
    }

    /**
     * Valida os dados da resposta
     * @param {AvaliacaoResposta} resposta Dados da resposta para ser validada
     * 
     * @returns {AvaliacaoRespostaErros} Retorna um objeto contendo os erros da resposta
     */
    _validarRespostaAlteracao(resposta) {
        /** @type {AvaliacaoRespostaErros} */
        const result = {
            erroResposta: '',
            erroOpcoes: '',
            id: '',
            resposta: '',
            valido: true,
        };

        if (!resposta) {
            result.erroResposta = 'Resposta é necessária';
            result.valido = false;
        } else {
            if (!resposta.id) {
                result.id = 'É necessário';
                result.valido = false;
            } else if (resposta.obrigatoria) {
                if (resposta.tipo === TIPO_FOTO) {
                    if (!this._validarValorPreenchido(resposta.novaImagem) && !this._validarValorPreenchido(resposta.resposta)) {
                        result.resposta = 'É necessário';
                        result.valido = false;
                    }
                } else if (resposta.tipo === TIPO_ESCOLHA || resposta.tipo === TIPO_MULTIPLAESCOLHA) {
                    if (!this._validarEscolhaPreenchida(resposta)) {
                        result.resposta = 'É necessário';
                        result.valido = false;
                    }
                } else {
                    if (!this._validarValorPreenchido(resposta.resposta)) {
                        result.resposta = 'É necessário';
                        result.valido = false;
                    }
                }
            }

            if (result.valido && resposta.tipo === TIPO_MULTIPLAESCOLHA && resposta.resposta) {
                if (!Array.isArray(resposta.resposta)) {
                    result.erroResposta = 'Resposta precisa ser um array';
                    result.valido = false;
                } else {
                    result.resposta = [];
                    //Valida as opções individualmente
                    for (let i = 0; i < resposta.resposta.length; i++) {
                        //Valida a resposta
                        let erroOpcao = this._validarOpcaoAlteracao(resposta.resposta[i]);
                        //Adiciona os erros na lista
                        result.resposta.push(erroOpcao);
                        //Caso a opção possua erros, todo o objeto é marcado como inválido
                        if (erroOpcao)
                            result.valido = false;
                    }
                }
            }
        }

        return result;
    }

    /**
     * Valida as respostas do tipo 'escolha' e 'multiplaEscolha',
     * considerando a opção 'outro'
     * @param {AvaliacaoResposta} resposta resposta a ser validada
     * 
     * @returns {Boolean} Retorna `true` caso a resposta esteja válida
     */
    _validarEscolhaPreenchida(resposta) {
        const outroPreenchido = this._validarValorPreenchido(resposta.outro);
        const respostaPreenchida = this._validarValorPreenchido(resposta.resposta);
        if (resposta.opcaoOutros) {
            return outroPreenchido || respostaPreenchida;
        } else {
            return respostaPreenchida;
        }
    }

    /**
     * Valida se um valor está preenchido.
     * Caso seja uma string, remove os espaços no final
     * Caso seja um array valida se há itens
     * @param {*} valor Valor a ser validado
     * 
     * @returns {Boolean} Retorna `true` caso o valor esteja preenchido
     */
    _validarValorPreenchido(valor) {
        if (!valor)
            return false;
        else if (typeof valor === 'string' && !valor.trim())
            return false;
        else if (Array.isArray(valor) && valor.length === 0)
            return false;
        else
            return true;
    }

    /**
     * Valida se a opção de resposta escolhida é um valor válido
     * 
     * @param {Number} opcao Valor a ser validado
     * 
     * @returns {Boolean} Retorna `true` caso a opção seja um valor válido
     */
    _validarOpcaoAlteracao(opcao) {
        if (!opcao || isNaN(opcao))
            return 'Opção inválida';
        else
            return '';
    }

    /**
     * Retorna os campos da avaliação necessários para envio para a API
     * @param {Avaliacao} avaliacao Dados da avaliação a ser tratado
     * 
     * @returns {AvaliacaoAlteracao} Retorna os dados da avalição tratados
     */
    _extrairCamposAlteracao(avaliacao) {
        /** @type {AvaliacaoAlteracao} */
        const result = {};

        if (avaliacao.status)
            result.status = avaliacao.status;

        if (avaliacao.secoes) {
            result.secoes = avaliacao.secoes.map(secao => this._extrairCamposSecaoAlteracao(secao));
        }

        return result;
    }

    /**
     * Retorna os campos da seção necessários para envio para a API
     * @param {AvaliacaoSecao} secao Dados da seção a ser tratada
     * 
     * @returns {AvaliacaoSecaoAlteracao} Retorna os dados da seção tratados
     */
    _extrairCamposSecaoAlteracao(secao) {
        /** @type {AvaliacaoSecaoAlteracao} */
        const result = {};

        result.id = secao.id;

        if (secao.questoes)
            result.respostas = secao.questoes.map(resposta => this._extrairCamposRespostaAlteracao(resposta));

        return result;
    }

    /**
     * Retorna os campos da resposta necessários para envio para a API
     * @param {AvaliacaoResposta} resposta Dados da resposta a ser tradada
     * 
     * @returns {AvaliacaoRespostaAlteracao} Retorna os dados da resposta tratados
     */
    _extrairCamposRespostaAlteracao(resposta) {
        /** @type {AvaliacaoRespostaAlteracao} */
        const result = {};

        result.id = resposta.id;

        //Para imagem, utiliza o campo novaImagem
        if (resposta.tipo === TIPO_FOTO) {
            if (!resposta.resposta || resposta.novaImagem)
                result.resposta = resposta.novaImagem;
            else
                return null;
        } else if (resposta.tipo === TIPO_INTEIRO || resposta.tipo === TIPO_NUMERICO) {
            result.resposta = converterNumero(resposta.resposta);
            if (isNaN(result.resposta))
                result.resposta = null;
        } else
            result.resposta = resposta.resposta;

        if (resposta.opcaoOutros) {
            if (resposta.exibeGrafico)
                result.outro = converterNumero(resposta.outro);
            else
                result.outro = resposta.outro;
        }

        return result;
    }

}

export default AvaliacaoController;